import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Camera, Check, AlertCircle } from 'lucide-react';

// Match PartnerOnboardingWizard exactly so the editor speaks the same
// vocabulary the partner saw at signup.
const PARTNER_TYPES = [
  { value: 'cbv', label: 'Chartered Business Valuator (CBV)' },
  { value: 'business_broker', label: 'Business Broker' },
  { value: 'lender', label: 'Commercial Lender' },
  { value: 'lawyer', label: 'Business Lawyer' },
  { value: 'accountant', label: 'Accountant / CPA' },
  { value: 'cepa_coach', label: 'CEPA-Certified Exit Planner' },
];

const REGIONS = [
  'British Columbia', 'Alberta', 'Saskatchewan', 'Manitoba',
  'Ontario', 'Quebec', 'Atlantic', 'Canada-wide',
];

const INDUSTRIES = [
  'Manufacturing', 'Technology', 'Healthcare', 'Retail',
  'Professional Services', 'Construction', 'Food & Hospitality',
  'Transportation', 'Financial Services', 'Real Estate',
  'Agriculture', 'SaaS', 'E-Commerce',
];

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

function ChipPicker({ label, options, selected, onToggle, helpText }) {
  return (
    <div>
      <label className="block text-[13px] text-muted-foreground mb-2">{label}</label>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const isSelected = selected.includes(opt);
          return (
            <button
              key={opt}
              type="button"
              onClick={() => onToggle(opt)}
              className={`rounded-full px-4 py-2 text-[13px] transition-all ${
                isSelected
                  ? 'bg-[hsl(var(--succeedo-orange))]/10 border border-[hsl(var(--succeedo-orange))] text-[hsl(var(--succeedo-orange))]'
                  : 'border border-border text-muted-foreground hover:border-muted-foreground'
              }`}
            >
              {opt}
            </button>
          );
        })}
      </div>
      {helpText && <p className="text-[12px] text-muted-foreground/70 mt-2">{helpText}</p>}
    </div>
  );
}

function TextField({ label, value, onChange, placeholder, type = 'text', maxLength }) {
  return (
    <div>
      <label className="block text-[13px] text-muted-foreground mb-2">{label}</label>
      <input
        type={type}
        value={value}
        maxLength={maxLength}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-card border border-border rounded-[8px] px-4 py-3 text-foreground placeholder:text-muted-foreground/50 focus:border-[hsl(var(--succeedo-orange))] focus:outline-none transition-colors"
      />
    </div>
  );
}

function TextArea({ label, value, onChange, placeholder, maxLength = 280, rows = 3 }) {
  return (
    <div>
      <label className="block text-[13px] text-muted-foreground mb-2">{label}</label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        maxLength={maxLength}
        className="w-full bg-card border border-border rounded-[8px] px-4 py-3 text-foreground placeholder:text-muted-foreground/50 focus:border-[hsl(var(--succeedo-orange))] focus:outline-none transition-colors resize-none"
      />
      <div className="text-[11px] text-muted-foreground/60 text-right mt-1">
        {(value || '').length}/{maxLength}
      </div>
    </div>
  );
}

function Toggle({ label, helpText, checked, onChange }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <div className="text-[14px] font-medium text-foreground">{label}</div>
        {helpText && <div className="text-[12px] text-muted-foreground mt-1">{helpText}</div>}
      </div>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 mt-1 ${
          checked ? 'bg-[hsl(var(--succeedo-orange))]' : 'bg-border'
        }`}
        aria-label={`Toggle ${label}`}
      >
        <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-0.5'
        }`} />
      </button>
    </div>
  );
}

const PartnerProfileEditor = ({ coachProfile, onSave, onUploadAvatar, loading }) => {
  // ── State ─────────────────────────────────────────────────────────
  const [name, setName] = useState('');
  const [headline, setHeadline] = useState('');
  const [bio, setBio] = useState('');
  const [orgName, setOrgName] = useState('');
  const [partnerCategory, setPartnerCategory] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [phone, setPhone] = useState('');
  const [regions, setRegions] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [acceptsIntros, setAcceptsIntros] = useState(true);
  const [avatarUrl, setAvatarUrl] = useState('');
  const [avatarErrored, setAvatarErrored] = useState(false);

  // UI state
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);

  // Reset the broken-image flag whenever the URL changes so a re-upload
  // gets a fresh attempt instead of inheriting the previous failure.
  useEffect(() => {
    setAvatarErrored(false);
  }, [avatarUrl]);

  // ── Initialize from coachProfile ──────────────────────────────────
  useEffect(() => {
    if (!coachProfile) return;
    setName(coachProfile.name || '');
    setHeadline(coachProfile.headline || '');
    setBio(coachProfile.bio || '');
    setOrgName(coachProfile.org_name || '');
    setPartnerCategory(coachProfile.partner_category || '');
    setContactEmail(coachProfile.contact_email || '');
    setWebsiteUrl(coachProfile.website_url || '');
    setPhone(coachProfile.phone || '');
    setRegions(coachProfile.geography_regions || []);
    setIndustries(coachProfile.industry_tags || []);
    setAcceptsIntros(coachProfile.accepts_introductions !== false);
    setAvatarUrl(coachProfile.avatar_url || '');
  }, [coachProfile]);

  // ── Change detection ───────────────────────────────────────────────
  const initialValues = useMemo(() => {
    if (!coachProfile) return null;
    return {
      name: coachProfile.name || '',
      headline: coachProfile.headline || '',
      bio: coachProfile.bio || '',
      orgName: coachProfile.org_name || '',
      partnerCategory: coachProfile.partner_category || '',
      contactEmail: coachProfile.contact_email || '',
      websiteUrl: coachProfile.website_url || '',
      phone: coachProfile.phone || '',
      regions: coachProfile.geography_regions || [],
      industries: coachProfile.industry_tags || [],
      acceptsIntros: coachProfile.accepts_introductions !== false,
    };
  }, [coachProfile]);

  const hasChanges = useMemo(() => {
    if (!initialValues) return false;
    return (
      name !== initialValues.name ||
      headline !== initialValues.headline ||
      bio !== initialValues.bio ||
      orgName !== initialValues.orgName ||
      partnerCategory !== initialValues.partnerCategory ||
      contactEmail !== initialValues.contactEmail ||
      websiteUrl !== initialValues.websiteUrl ||
      phone !== initialValues.phone ||
      JSON.stringify(regions) !== JSON.stringify(initialValues.regions) ||
      JSON.stringify(industries) !== JSON.stringify(initialValues.industries) ||
      acceptsIntros !== initialValues.acceptsIntros
    );
  }, [
    initialValues,
    name, headline, bio, orgName, partnerCategory,
    contactEmail, websiteUrl, phone, regions, industries, acceptsIntros,
  ]);


  // ── Handlers ───────────────────────────────────────────────────────
  const toggleArrayItem = (current, setter, item) => {
    if (current.includes(item)) {
      setter(current.filter((i) => i !== item));
    } else {
      setter([...current, item]);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setUploadError('Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image.');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setUploadError(`File too large (${(file.size / (1024 * 1024)).toFixed(1)}MB). Maximum size is 5MB.`);
      return;
    }

    setUploadError(null);
    setUploading(true);
    try {
      const result = await onUploadAvatar(file);
      if (result?.avatar_url) setAvatarUrl(result.avatar_url);
      if (window.ahoy) window.ahoy.track('succeedo_partner_profile_avatar_uploaded');
    } catch (err) {
      setUploadError(err.message || 'Failed to upload avatar. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSaveError(null);
    setSaved(false);
    try {
      await onSave({
        name: name || null,
        headline: headline || null,
        bio: bio || null,
        orgName: orgName || null,
        partnerCategory: partnerCategory || null,
        contactEmail: contactEmail || null,
        websiteUrl: websiteUrl || null,
        phone: phone || null,
        geographyRegions: regions,
        industryTags: industries,
        acceptsIntroductions: acceptsIntros,
      });
      setSaved(true);
      if (window.ahoy) window.ahoy.track('succeedo_partner_profile_saved');
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setSaveError(err.message || 'Failed to save profile. Please try again.');
    }
  };

  if (!coachProfile) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-muted-foreground text-[16px]">Loading profile…</div>
      </div>
    );
  }

  return (
    <div className="max-w-[800px] mx-auto p-6 md:p-10">
      <header className="mb-8">
        <h1 className="font-serif text-[32px] font-light text-foreground mb-2">Edit your partner profile</h1>
        <p className="text-[14px] text-muted-foreground">
          Sellers see this on the partner directory. Keep it current — your bio, regions and
          industries determine which intros come your way.
        </p>
      </header>

      {/* Avatar */}
      <section className="mb-10 flex items-center gap-6">
        <div className="relative">
          <div className="w-24 h-24 rounded-full bg-muted flex items-center justify-center overflow-hidden">
            {avatarUrl && !avatarErrored ? (
              <img
                src={avatarUrl}
                alt={name}
                onError={() => setAvatarErrored(true)}
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-[28px] font-semibold text-foreground">{getInitials(name)}</span>
            )}
          </div>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-[hsl(var(--succeedo-orange))] text-white flex items-center justify-center hover:bg-[hsl(var(--succeedo-orange))]/90 transition-colors disabled:opacity-50"
            aria-label="Upload avatar"
          >
            <Camera className="w-4 h-4" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={handleFileChange}
            className="hidden"
          />
        </div>
        <div>
          <div className="text-[15px] font-medium text-foreground">{name || 'Your name'}</div>
          <div className="text-[13px] text-muted-foreground">
            {uploading ? 'Uploading…' : 'JPEG, PNG, GIF or WebP · max 5MB'}
          </div>
          {uploadError && (
            <div className="text-[12px] text-red-500 mt-1 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {uploadError}
            </div>
          )}
        </div>
      </section>

      {/* Identity */}
      <section className="space-y-5 mb-10">
        <h2 className="font-mono text-[11px] uppercase text-muted-foreground/60 tracking-wide">Identity</h2>
        <TextField label="Full name" value={name} onChange={setName} placeholder="Jane Doe" />
        <TextField label="Headline" value={headline} onChange={setHeadline} placeholder="CEPA-certified exit planner" maxLength={120} />
        <TextField label="Firm or organization" value={orgName} onChange={setOrgName} placeholder="Acme Advisory" />
        <div>
          <label className="block text-[13px] text-muted-foreground mb-2">Partner category</label>
          <select
            value={partnerCategory}
            onChange={(e) => setPartnerCategory(e.target.value)}
            className="w-full bg-card border border-border rounded-[8px] px-4 py-3 text-foreground focus:border-[hsl(var(--succeedo-orange))] focus:outline-none"
          >
            <option value="">Select a category…</option>
            {PARTNER_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
      </section>

      {/* Contact */}
      <section className="space-y-5 mb-10">
        <h2 className="font-mono text-[11px] uppercase text-muted-foreground/60 tracking-wide">Contact</h2>
        <TextField label="Contact email" type="email" value={contactEmail} onChange={setContactEmail} placeholder="you@firm.com" />
        <TextField label="Website" type="url" value={websiteUrl} onChange={setWebsiteUrl} placeholder="https://example.com" />
        <TextField label="Phone" type="tel" value={phone} onChange={setPhone} placeholder="+1-416-555-1212" />
      </section>

      {/* About + targeting */}
      <section className="space-y-6 mb-10">
        <h2 className="font-mono text-[11px] uppercase text-muted-foreground/60 tracking-wide">Practice</h2>
        <TextArea
          label="One-line bio"
          value={bio}
          onChange={setBio}
          placeholder="e.g. I value Canadian SMBs across professional services and manufacturing."
        />
        <ChipPicker
          label="Regions you serve"
          options={REGIONS}
          selected={regions}
          onToggle={(r) => toggleArrayItem(regions, setRegions, r)}
          helpText="Select all that apply."
        />
        <ChipPicker
          label="Industry focus"
          options={INDUSTRIES}
          selected={industries}
          onToggle={(i) => toggleArrayItem(industries, setIndustries, i)}
          helpText="Sellers filter by industry when browsing the directory."
        />
      </section>

      {/* Visibility */}
      <section className="mb-12 p-5 rounded-[12px] border border-border">
        <Toggle
          label="List me in the partner directory"
          helpText="When on, sellers can request introductions to you."
          checked={acceptsIntros}
          onChange={setAcceptsIntros}
        />
      </section>

      {/* Save bar */}
      <div className="sticky bottom-4 bg-card/90 backdrop-blur-md border border-border rounded-[12px] p-4 flex items-center justify-between gap-4">
        <div className="text-[13px] text-muted-foreground">
          {saveError && (
            <span className="text-red-500 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" /> {saveError}
            </span>
          )}
          {!saveError && saved && (
            <span className="text-[hsl(var(--succeedo-orange))] flex items-center gap-1">
              <Check className="w-3 h-3" /> Saved
            </span>
          )}
          {!saveError && !saved && hasChanges && 'You have unsaved changes.'}
          {!saveError && !saved && !hasChanges && 'All changes saved.'}
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={!hasChanges || loading}
          className="bg-foreground text-background rounded-full px-6 py-2.5 text-[14px] font-medium hover:bg-foreground/90 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {loading ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
};

export default PartnerProfileEditor;
